import clsx from 'clsx'
import compact from 'lodash/compact'
import map from 'lodash/map'
import { useRouter } from 'next/router'
import React, { FC, forwardRef, MutableRefObject, useContext, useMemo } from 'react'

import { ImageV4 } from '@Components/Image'
import { Link } from '@Components/Link'
import { PaymentIcons } from '@Components/Payment/PaymentIcons'
import { TrustIcons } from '@Components/TrustIcons'
import { TrustPilotWidget } from '@Components/TrustPilotWidget'
import { ClubSignUpBanner } from '@Domains/Club/Components/ClubSignupBanner'
import { getMenuItemTitle } from '@Domains/Navigation/Features/Menu/Menu'
import { PageType } from '@Generated/graphql'
import { useCMSModalOpen } from '@Globals/CMS/Modal/Hook'
import { MetadataContext } from '@Globals/Metadata'
import { useGroupVisibilityRef } from '@Globals/ScrollPosition'
import { useTranslation } from '@Globals/Translations'
import { useCustomerSupportData } from '@Globals/useCustomerSupportData'

import styles from './Footer.module.scss'

interface FooterLinkItem {
  action?: () => void
  analytics?: any
  href?: string
  label?: string
  newTab?: boolean
  title: string
}

const FooterNavigationItem: FC<{ className?: string, item: FooterLinkItem }> = ({
  className,
  item,
}) => {
  const { t } = useTranslation()
  return (item.action
    ? (
      <button className={clsx(styles.menuitems, className)} onClick={item.action} type="button">
        {t(item?.title, item?.title)}
      </button>
    )
    : (
      <Link
        key={item?.title}
        analytics={item?.analytics}
        className={clsx(styles.menuitems, className)}
        href={item?.href}
        rel={item?.newTab ? 'noopener noreferrer' : undefined}
        target={item?.newTab ? '_blank' : undefined}
      >
        {t(item?.title, item?.title)}
      </Link>
    ))
}

const Socials: FooterLinkItem[] = [
  {
    label: 'Instragram',
    href: 'https://www.instagram.com/trendhim',
    newTab: true,
    title: 'footer:INSTAGRAM_LABEL',
  },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/trendhim',
    newTab: true,
    title: 'footer:YOUTUBE_LABEL',
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/trendhim',
    newTab: true,
    title: 'footer:FACEBOOK_LABEL',
  },
]

export const Footer = forwardRef((props, ref) => {
  const {
    cmsData,
    featuredPosts,
    navigation,
  } = useContext(MetadataContext)
  const articles = useMemo(() => featuredPosts.find(fp => fp.type === 'articles'), [featuredPosts])
  const { store, storeURL } = useContext(MetadataContext)
  const router = useRouter()
  const hideClub = router.pathname.includes('account/overview')
  const phone = store?.config?.phone || ''
  const openModal = useCMSModalOpen()
  const {
    customerSupportDataMap: {
      about,
      career,
      faq,
      returnsLogin,
    },
  } = useCustomerSupportData({ sourceID: 'footer_customer_support' })

  const infoData: FooterLinkItem[] = useMemo(() => [
    {
      ...faq,
      title: faq?.title || '',
    },
    {
      title: cmsData.urls.giftGuide?.title || '',
      href: cmsData.urls.giftGuide?.url || undefined,
    },
    {
      ...returnsLogin,
      title: returnsLogin?.title || '',
    },
    {
      title: 'footer:SEE_SHIPPING_OPTIONS',
      action: () => openModal(PageType.Modal, '20', 'footer'),
    },
    {
      title: 'footer:FOOTER_CONTACT_TITLE',
      action: () => openModal(PageType.Modal, 'contact-us', 'footer'),
    },
  ], [cmsData.urls.giftGuide?.title, cmsData.urls.giftGuide?.url, faq, returnsLogin, openModal])

  const aboutUsData: FooterLinkItem[] = useMemo(() => compact([
    {
      title: 'TrendhimClub',
      href: storeURL('account/benefits'),
      analytics: {
        dest_page_type: 'trendhim_club',
        dest_page_type_id: '',
        source_id: 'footer',
      },
    },
    {
      ...about,
      title: about?.title || '',
    },
    {
      ...career,
      title: career?.title || '',
    },
    {
      title: 'footer:NEW_ARTICLES_LABEL',
      href: articles?.url,
      analytics: {
        dest_page_type: 'footer_category_articles',
        dest_page_type_id: '',
        source_id: 'footer',
      },
    },
    {
      title: 'footer:PRESS_TITLE',
      href: cmsData.urls.press?.url || '',
    },
    {
      title: 'footer:CSR_TITLE',
      href: cmsData.urls.csr?.url || '',
    },
  ]), [about, articles?.url, career, cmsData.urls.csr?.url, cmsData.urls.press?.url, storeURL])

  const policyAndTerms: FooterLinkItem[] = useMemo(() => [
    {
      title: 'footer:COOKIES_POLICY',
      href: cmsData?.urls?.cookiePolicy?.url || '',
    },
    {
      title: 'footer:PRIVACY_POLICY',
      href: cmsData?.urls?.privacyPolicy?.url || '',
    },
    {
      title: 'footer:CUSTOMISE_COOKIES',
      action: () => (window as any).UC_UI.showSecondLayer(),
    },
    {
      title: 'footer:TERMS_AND_CONDITIONS',
      href: cmsData.urls.terms?.url || '',
    },
  ], [cmsData.urls?.cookiePolicy?.url, cmsData.urls?.privacyPolicy?.url, cmsData.urls.terms?.url])
  const { t, tDanger } = useTranslation()

  return (
    <footer className={styles.footer} ref={ref as MutableRefObject<HTMLDivElement>}>
      {!hideClub && (
        <ClubSignUpBanner className={styles.footerClubBanner}>
          <span className={styles.clubText}>
            <p {...tDanger('common:TRENDHIMCLUB_MAIN_CTA_1_DYNAMIC', { percentage: '15' })} />
            <Link className={styles.learnMore} href={storeURL('account/benefits')}>{t('common:TRENDHIMCLUB_LEARN_MORE_LABEL')}</Link>
          </span>
        </ClubSignUpBanner>
      )}
      <div className={clsx(styles.newsContainer, styles.mobile)}>
        <span className={styles.socialfooter}>
          {map(Socials, item => <FooterNavigationItem key={item.label} item={item} />)}
        </span>
      </div>
      <div className={styles.infoContainer}>
        <div className={styles.menus}>
          <div className={styles.menusItems}>
            <div className={styles.menusItem}>
              <div className={styles.menu}>
                <h2 className={styles.menutitle}>{t('footer:CATEGORIES')}</h2>
                {map(navigation, menuItem => (
                  <Link
                    key={menuItem.id}
                    className={styles.menuitems}
                    href={menuItem.link?.url || ''}
                  >
                    {getMenuItemTitle(menuItem)}
                  </Link>
                ))}
              </div>
              <div className={styles.menu}>
                <h2 className={styles.menutitle}>{t('footer:CUSTOMER_SUPPORT')}</h2>
                {map(infoData, item => (item.href || item.action) && <FooterNavigationItem key={item.title} item={item} />)}
              </div>
              <div className={styles.menu}>
                <h2 className={styles.menutitle}>{t('footer:FOOTER_ABOUT_US_TITLE')}</h2>
                {map(aboutUsData, item => (item.href || item.action) && <FooterNavigationItem key={item.title} item={item} />)}
              </div>
            </div>
            {(store.id === '20' || store.id === '19') && (
              <div className={clsx(styles.store, styles.storeDesktop)}>
                <span className={styles.trendhimAps}>Trendhim ApS</span>
                <span className={styles.adresse}>Egeskovvej 12, 8700 Horsens</span>
                <Link
                  analytics={{
                    dest_page_type: 'contact',
                    dest_page_type_id: '',
                    source_id: 'footer_contact_us',
                  }}
                  className={styles.email}
                  href={`mailto:${store?.config?.email}`}
                >
                  {store?.config?.email}
                </Link>
                <Link
                  className={styles.phoneNumber}
                  href={`tel:${phone.replace(' ', '')}`}
                >
                  {store?.config?.phone}
                </Link>
              </div>
            )}
          </div>
          <div className={styles.trustpilot}>
            <TrustPilotWidget />
            <span className={styles.trustIcon}><TrustIcons variant="footer" /></span>
          </div>

        </div>

      </div>
      <div className={clsx(styles.newsContainer, styles.desktop)}>
        <div className={styles.joinTrendHim}>
          <ImageV4
            alt="Trendhim"
            className={clsx(styles.trendhimImg, styles.trendhimImgDesktop)}
            src="https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/blt59296545a01439bf/trendhim-logo-with-text-v2.svg"
            width={180}
          />
        </div>
        <span className={styles.socialfooter}>
          {map(Socials, item => <FooterNavigationItem key={item.label} item={item} />)}
        </span>
      </div>
      <div className={styles.logoTrendhim}>
        <img
          alt="Trendhim"
          className={clsx(styles.trendhimImg, styles.trendhimImgMobile)}
          src="https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/blt59296545a01439bf/trendhim-logo-with-text-v2.svg"
        />
      </div>
      {(store.id === '20' || store.id === '19') && (
        <div className={clsx(styles.store, styles.storeMobile)}>
          <span className={styles.trendhimAps}>Trendhim ApS</span>
          <span className={styles.adresse}>Egeskovvej 12, 8700 Horsens</span>
          <Link
            analytics={{
              dest_page_type: 'contact',
              dest_page_type_id: '',
              source_id: 'footer_contact_us',
            }}
            className={styles.email}
            href={`mailto:${store?.config?.email}`}
          >
            {store?.config?.email}
          </Link>
          <Link
            className={styles.phoneNumber}
            href={`tel:${phone.replace(' ', '')}`}
          >
            {store?.config?.phone}
          </Link>
        </div>
      )}
      <div className={styles.policyContainer}>
        <span className={styles.policyAndTerms}>
          {map(policyAndTerms, item => <FooterNavigationItem key={item.title} item={item} />)}
        </span>
        <PaymentIcons className={styles.cardIcons} />
      </div>
    </footer>
  )
})

export const FooterWithGroupVisibility = () => {
  const ref = useGroupVisibilityRef('footer')

  return useMemo(() => <Footer ref={ref} />, [ref])
}
